import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getServices,
  getService,
  createService,
  updateService,
  deleteService,
  seedServices,
  updateServicesAdminEmails,
  disableAllServicesNotifications,
  updateServiceSchedulerCron,
  getServicesPeriodExamples,
  getProjectServices,
  getProjectService,
  createProjectService,
  updateProjectService,
  deleteProjectService,
  toggleProjectServiceScheduler,
  toggleProjectServiceAutoApproval,
  toggleProjectServiceEmailAdmin,
  toggleProjectServiceEmailAssociate,
  toggleProjectServiceEnabled,
  updateProjectServiceAdminEmails,
} from "../api/servicesApi";
import { showAlert } from "../slices/alertsSlice";
import {
  setServicesList,
  setCurrentService,
  setProjectServicesList,
  setCurrentProjectService,
  setPeriodExamples,
} from "../slices/servicesSlice";
import { handleError } from "../slices/errorsSlice";
import { startLoading, endLoading } from "../slices/loadingSlice";

// ============ GLOBAL SERVICES ============

export const getServicesList = createAsyncThunk(
  "services/getServicesList",
  ({ query = {} }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    getServices(token, query)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => dispatch(setServicesList({ services: data.services })))
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

export const getCurrentService = createAsyncThunk(
  "services/getCurrentService",
  ({ id }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    getService(token, id)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => dispatch(setCurrentService({ service: data.service })))
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

export const createNewService = createAsyncThunk(
  "services/createNewService",
  ({ service, doubleFactorCode, callback }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    createService(token, service, doubleFactorCode)
      .then((response) =>
        response.status === 201 ? response.json() : Promise.reject(response)
      )
      .then((data) => callback(data.service_id))
      .then(() =>
        dispatch(showAlert({ message: "Servicio creado", type: "success" }))
      )
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

export const updateCurrentService = createAsyncThunk(
  "services/updateCurrentService",
  ({ id, service }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    updateService(token, id, service)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => dispatch(setCurrentService({ service: data.service })))
      .then(() =>
        dispatch(
          showAlert({ message: "Servicio actualizado", type: "success" })
        )
      )
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

export const deleteCurrentService = createAsyncThunk(
  "services/deleteCurrentService",
  ({ id, callback }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    deleteService(token, id)
      .then((response) =>
        response.status === 200 ? true : Promise.reject(response)
      )
      .then(() => callback())
      .then(() =>
        dispatch(showAlert({ message: "Servicio eliminado", type: "success" }))
      )
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

export const seedDefaultServices = createAsyncThunk(
  "services/seedDefaultServices",
  ({ defaultConfig = {}, callback }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    seedServices(token, defaultConfig)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => callback(data))
      .then(() =>
        dispatch(
          showAlert({
            message: "Servicios predeterminados creados",
            type: "success",
          })
        )
      )
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

export const updateAllServicesAdminEmails = createAsyncThunk(
  "services/updateAllServicesAdminEmails",
  ({ adminEmails, callback }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    updateServicesAdminEmails(token, adminEmails)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => callback(data))
      .then(() =>
        dispatch(
          showAlert({
            message: "Emails de administrador actualizados",
            type: "success",
          })
        )
      )
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

export const disableAllNotifications = createAsyncThunk(
  "services/disableAllNotifications",
  ({ callback }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    disableAllServicesNotifications(token)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => callback(data))
      .then(() =>
        dispatch(
          showAlert({
            message: "Notificaciones deshabilitadas",
            type: "success",
          })
        )
      )
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

export const updateServiceCron = createAsyncThunk(
  "services/updateServiceCron",
  ({ id, cron, timezone }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    updateServiceSchedulerCron(token, id, cron, timezone)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => dispatch(setCurrentService({ service: data.service })))
      .then(() =>
        dispatch(
          showAlert({ message: "Cron actualizado", type: "success" })
        )
      )
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

export const getPeriodExamples = createAsyncThunk(
  "services/getPeriodExamples",
  (_, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    getServicesPeriodExamples(token)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => dispatch(setPeriodExamples({ examples: data.examples })))
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

// ============ PROJECT SERVICES ============

export const getProjectServicesList = createAsyncThunk(
  "services/getProjectServicesList",
  ({ projectId }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    getProjectServices(token, projectId)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) =>
        dispatch(setProjectServicesList({ services: data.services }))
      )
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

export const getCurrentProjectService = createAsyncThunk(
  "services/getCurrentProjectService",
  ({ projectId, serviceId }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    getProjectService(token, projectId, serviceId)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) =>
        dispatch(setCurrentProjectService({ service: data.service }))
      )
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

export const createNewProjectService = createAsyncThunk(
  "services/createNewProjectService",
  ({ projectId, service, callback }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    createProjectService(token, projectId, service)
      .then((response) =>
        response.status === 201 ? response.json() : Promise.reject(response)
      )
      .then((data) => callback(data.service_id))
      .then(() =>
        dispatch(showAlert({ message: "Servicio creado", type: "success" }))
      )
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

export const updateCurrentProjectService = createAsyncThunk(
  "services/updateCurrentProjectService",
  ({ projectId, serviceId, service, callback }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    updateProjectService(token, projectId, serviceId, service)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => {
        dispatch(setCurrentProjectService({ service: data.service }));
        if (callback) callback(data);
      })
      .then(() =>
        dispatch(
          showAlert({ message: "Servicio actualizado", type: "success" })
        )
      )
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

export const deleteCurrentProjectService = createAsyncThunk(
  "services/deleteCurrentProjectService",
  ({ projectId, serviceId, callback }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    deleteProjectService(token, projectId, serviceId)
      .then((response) =>
        response.status === 200 ? true : Promise.reject(response)
      )
      .then(() => callback())
      .then(() =>
        dispatch(showAlert({ message: "Servicio eliminado", type: "success" }))
      )
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

export const toggleServiceScheduler = createAsyncThunk(
  "services/toggleServiceScheduler",
  ({ projectId, serviceId, enabled, onSuccess, onFail }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    toggleProjectServiceScheduler(token, projectId, serviceId, enabled)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => {
        dispatch(setCurrentProjectService({ service: data.service }));
        dispatch(
          showAlert({ message: "Scheduler actualizado", type: "success" })
        );
        if (onSuccess) onSuccess(data);
      })
      .catch((err) => {
        dispatch(handleError(err));
        if (onFail) onFail(err);
      })
      .finally(() => dispatch(endLoading()));
  }
);

export const toggleServiceAutoApproval = createAsyncThunk(
  "services/toggleServiceAutoApproval",
  ({ projectId, serviceId, enabled, onSuccess, onFail }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    toggleProjectServiceAutoApproval(token, projectId, serviceId, enabled)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => {
        dispatch(setCurrentProjectService({ service: data.service }));
        dispatch(
          showAlert({ message: "Auto-aprobación actualizada", type: "success" })
        );
        if (onSuccess) onSuccess(data);
      })
      .catch((err) => {
        dispatch(handleError(err));
        if (onFail) onFail(err);
      })
      .finally(() => dispatch(endLoading()));
  }
);

export const toggleServiceEmailAdmin = createAsyncThunk(
  "services/toggleServiceEmailAdmin",
  ({ projectId, serviceId, enabled, onSuccess, onFail }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    toggleProjectServiceEmailAdmin(token, projectId, serviceId, enabled)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => {
        dispatch(setCurrentProjectService({ service: data.service }));
        dispatch(
          showAlert({ message: "Email admin actualizado", type: "success" })
        );
        if (onSuccess) onSuccess(data);
      })
      .catch((err) => {
        dispatch(handleError(err));
        if (onFail) onFail(err);
      })
      .finally(() => dispatch(endLoading()));
  }
);

export const toggleServiceEmailAssociate = createAsyncThunk(
  "services/toggleServiceEmailAssociate",
  ({ projectId, serviceId, enabled, onSuccess, onFail }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    toggleProjectServiceEmailAssociate(token, projectId, serviceId, enabled)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => {
        dispatch(setCurrentProjectService({ service: data.service }));
        dispatch(
          showAlert({ message: "Email asociado actualizado", type: "success" })
        );
        if (onSuccess) onSuccess(data);
      })
      .catch((err) => {
        dispatch(handleError(err));
        if (onFail) onFail(err);
      })
      .finally(() => dispatch(endLoading()));
  }
);

export const toggleServiceEnabled = createAsyncThunk(
  "services/toggleServiceEnabled",
  ({ projectId, serviceId, enabled, onSuccess, onFail }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    toggleProjectServiceEnabled(token, projectId, serviceId, enabled)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => {
        dispatch(setCurrentProjectService({ service: data.service }));
        dispatch(
          showAlert({ message: "Servicio actualizado", type: "success" })
        );
        if (onSuccess) onSuccess(data);
      })
      .catch((err) => {
        dispatch(handleError(err));
        if (onFail) onFail(err);
      })
      .finally(() => dispatch(endLoading()));
  }
);

export const updateServiceAdminEmails = createAsyncThunk(
  "services/updateServiceAdminEmails",
  ({ projectId, serviceId, adminEmails }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    updateProjectServiceAdminEmails(token, projectId, serviceId, adminEmails)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) =>
        dispatch(setCurrentProjectService({ service: data.service }))
      )
      .then(() =>
        dispatch(
          showAlert({
            message: "Emails de administrador actualizados",
            type: "success",
          })
        )
      )
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);
